import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import classes from './jCodeModal.module.scss';
import api from '../../../../../../api/api';
import Utils from '../../../../../../utils/utils';
import Modal from '../../../../../_common/Modal/Modal';
import Button from '../../../../../_common/Form/Buttons/Button';
import InputField from '../../../../../_common/Form/TextFields/InputField';
import Select from '../../../../../_common/Form/Selects/Select/Select';
import SelectWithSearch from '../../../../../_common/Form/Selects/SelectWithSearch/SelectWithSearch';
import { ReactComponent as InfoIcon } from '../../../../../../assets/icons/info.svg';

const MEDICATION_LIMIT = 20;

const billingUnitOptions = ['MG', 'MCG', 'ML', 'GM', 'EA', 'UNIT', 'IU'];

const medicationTypeOptions = ['Brand', 'Generic', 'Immunotherapy', 'Biosimilars'];

const defaultData = {
  id: null,
  jCode: '',
  name: '',
  NDC: '',
  jQuantityPerFill: '',
  jUnitsPerFill: '',
  brand: '',
  active: true,
};

const JCodeModal = ({
  isOpen, jCode, onClose, onSubmit, loading,
}) => {
  const [requestData, setRequestData] = useState(defaultData);
  const [medications, setMedications] = useState([]);
  const [medicationSearch, setMedicationSearch] = useState('');
  const [medicationLoading, setMedicationLoading] = useState(false);

  const searchTimeout = useRef();

  const edit = !!jCode?.id;

  const getMedications = async (search) => {
    try {
      const { data: { data } } = await api.getProducts({
        page: 1,
        limit: MEDICATION_LIMIT,
        search: search.trim(),
        otc: 0,
      });

      setMedications(data.response || []);
    } catch (e) {
      setMedications([]);

      toast.error(e.response?.data?.message || 'Something went wrong');
    }
  };

  const changeMedicationSearch = (value) => {
    setMedicationSearch(value);

    clearTimeout(searchTimeout.current);

    if (!value.trim()) {
      setMedications([]);
      setMedicationLoading(false);

      return;
    }

    setMedicationLoading(true);

    searchTimeout.current = setTimeout(async () => {
      await getMedications(value);

      setMedicationLoading(false);
    }, 400);
  };

  const selectMedication = (product, name) => {
    setMedicationSearch(name || '');

    setRequestData((prev) => ({
      ...prev,
      id: product?.id || null,
      name: product?.name || '',
      NDC: product?.NDC || '',
      brand: product?.brand || '',
      active: product?.active === undefined ? true : !!product.active,
    }));
  };

  useEffect(() => () => clearTimeout(searchTimeout.current), []);

  useEffect(() => {
    if (!isOpen) return;

    clearTimeout(searchTimeout.current);

    setMedicationSearch('');
    setMedications([]);
    setMedicationLoading(false);

    setRequestData(edit
      ? {
        id: jCode.id,
        jCode: jCode.jCode || '',
        name: jCode.name || '',
        NDC: jCode.NDC || '',
        jQuantityPerFill: jCode.jQuantityPerFill || '',
        jUnitsPerFill: jCode.jUnitsPerFill || '',
        brand: jCode.brand || '',
        active: jCode.active === undefined ? true : !!jCode.active,
      }

      : defaultData);
  }, [isOpen, jCode]);

  const changeData = (path, value) => {
    setRequestData((prev) => ({ ...prev, [path]: value }));
  };

  const { active } = requestData;

  const filled = !!requestData.id
    && !!requestData.jCode.trim()
    && !!String(requestData.jQuantityPerFill).trim()
    && !!requestData.jUnitsPerFill;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className={classes.j_code_modal}
    >
      <div className={classes.head}>
        <h2>{edit ? 'Edit J-Code' : 'Add J-Code'}</h2>

        <p>
          {edit
            ? 'Update this billable J-code in the master list.'
            : 'Create a new billable J-code in the master list.'}
        </p>
      </div>

      <div className={classes.body}>
        <div className={classes.field}>
          <span className={classes.label}>
            J-Code

            <i>*</i>
          </span>

          <InputField
            placeholder="J1100"
            value={requestData.jCode}
            onChange={(value) => changeData('jCode', value)}
          />

          <p className={classes.hint}>HCPCS Level II drug code. Format J####.</p>
        </div>

        <div className={classes.field}>
          <span className={classes.label}>
            Medication Name

            <i>*</i>
          </span>

          {edit
            ? (
              <InputField
                disabled
                placeholder="Dexamethasone"
                wrapperClassName={classes.readonly}
                value={requestData.name}
              />
            )

            : (
              <SelectWithSearch
                hideClearButton
                zIndex={33}
                wrapperClassName={classes.medication_select}
                optionKey=""
                searchValue={medicationSearch}
                options={medications}
                loading={medicationLoading}
                onChange={changeMedicationSearch}
                onSelectOption={selectMedication}
              />
            )}
        </div>

        <div className={classes.field}>
          <span className={classes.label}>NDC</span>

          <InputField
            disabled
            placeholder="NDC"
            wrapperClassName={classes.readonly}
            value={requestData.NDC}
          />

          <p className={classes.hint}>Long ndc as it appears on the claim.</p>
        </div>

        <div className={classes.row}>
          <div className={classes.field}>
            <span className={classes.label}>
              Billing Quantity

              <i>*</i>
            </span>

            <InputField
              value={requestData.jQuantityPerFill}
              onChange={(value) => changeData('jQuantityPerFill', value)}
              onKeyPress={(e) => Utils.keyPressCurrency(e, false, undefined, 2)}
              onPaste={(e) => Utils.keyPressCurrency(e, true, undefined, 2)}
            />
          </div>

          <div className={classes.field}>
            <span className={classes.label}>
              Billing Unit

              <i>*</i>
            </span>

            <Select
              hideClearButton
              zIndex={33}
              options={billingUnitOptions}
              currentOption={requestData.jUnitsPerFill}
              onChange={(value) => changeData('jUnitsPerFill', value)}
            />
          </div>
        </div>

        <div className={classes.note}>
          <InfoIcon />

          <p>
            {'Quantity and unit are stored separately — '}

            <b>1 MG</b>

            {' and '}

            <b>50 MG</b>

            {' bill very differently on the same code.'}
          </p>
        </div>

        <div className={classes.field}>
          <span className={classes.label}>
            Medication Type
          </span>

          <Select
            disabled
            hideClearButton
            zIndex={33}
            placeholder="Generic"
            wrapperClassName={classes.readonly}
            options={medicationTypeOptions}
            currentOption={requestData.brand}
            onChange={(value) => changeData('brand', value)}
          />
        </div>

        <div className={classes.field}>
          <span className={classes.label}>Status</span>

          <div className={`${classes.toggle} ${classes.readonly_toggle}`}>
            <button
              type="button"
              disabled
              className={active ? `${classes.selected} ${classes.green}` : ''}
            >
              Active
            </button>

            <button
              type="button"
              disabled
              className={!active ? classes.selected : ''}
            >
              Inactive
            </button>
          </div>

          <p className={classes.hint}>
            Inactive J-codes cannot be billed going forward. Historical billing data is always retained.
          </p>
        </div>
      </div>

      <div className={classes.footer}>
        <Button
          title="Cancel"
          className={classes.btn}
          clearButton
          onClick={onClose}
        />

        <Button
          title="Save J-Code"
          className={classes.btn}
          loading={loading}
          disabled={!filled}
          reverseColor
          onClick={() => onSubmit(requestData)}
        />
      </div>
    </Modal>
  );
};

JCodeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  jCode: PropTypes.object,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default JCodeModal;
